import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const BrushUpEnglish = () => {
  const title = 'Brush Up on Your English';
  const image = '/Images/tutor2-1280x470.webp';
  const intro = 'Even experienced teachers need to keep their own English sharp. This guide targets the high-impact grammar, vocabulary, and pronunciation areas that most affect classroom credibility and explanation clarity.';

  const seoContent = [
    'Teacher language awareness isn\'t about passing a proficiency test—it\'s about being able to explain *why* we say \"I\'ve been waiting\" not \"I wait since 2 PM,\" or why \"the information is\" not \"are.\" Focus your self-study on the structures you explain most often: present perfect vs. past simple, conditionals, article rules, and modal nuances.',
    'Vocabulary-wise, prioritize classroom metalanguage (\"skim,\" \"scan,\" \"infer,\" \"collocate\"), high-frequency academic words (Coxhead\'s AWL), and the lexical sets your students struggle with (phrasal verbs, preposition patterns, false friends). A personal \"teacher lexicon\" spreadsheet—word, definition, example, common error—becomes a quick-reference goldmine.',
    'Pronunciation matters for modeling. Record yourself explaining a grammar point, then compare to a native-speaker model. Target word stress in multisyllabic academic words, sentence stress for emphasis, and intonation patterns for questions vs. statements. Even 5 minutes daily with a shadowing app builds muscle memory.',
    'Build a sustainable routine: one grammar deep-dive per week (explanation + 3 practice sentences), five new lexical items added to your lexicon, one pronunciation drill. Pair with a peer teacher for monthly \"language clinic\" swaps—you correct each other\'s boardwork samples.',
  ];

  const levels = [
    {
      level: 'grammar',
      label: 'Grammar Refreshers',
      tagline: 'Clear explanations + practice',
      cards: [
        {
          title: 'Present Perfect Masterclass',
          image: '/Images/grammar1.webp',
          desc: 'Timeline diagrams, typical student errors, and 20 contextualized practice sentences with keys.',
          links: [{ text: 'Start Module', to: '/grammar-exercises/present-perfect' }],
        },
        {
          title: 'Conditionals Roadmap',
          image: '/Images/2people1.webp',
          desc: 'Zero through mixed conditionals: form, meaning, teaching sequence, and concept-check questions.',
          links: [{ text: 'View Guide', to: '/grammar-exercises/conditionals' }],
        },
        {
          title: 'Article System Deep-Dive',
          image: '/Images/vocabulary.webp',
          desc: 'Count/non-count, generic vs. specific, geographic names, and the zero-article rule set.',
          links: [{ text: 'Study Module', to: '/grammar-exercises/articles' }],
        },
      ],
    },
    {
      level: 'vocab',
      label: 'Vocabulary & Metalanguage',
      tagline: 'Expand your teacher lexicon',
      cards: [
        {
          title: 'Classroom Metalanguage Glossary',
          image: '/Images/online-teacher.webp',
          desc: '100+ terms with definitions, phonemic script, and example teacher talk lines.',
          links: [{ text: 'Download PDF', to: '/resources/metalanguage-glossary.pdf' }],
        },
        {
          title: 'AWL Sublists for Teachers',
          image: '/Images/reading.webp',
          desc: 'Coxhead\'s Academic Word List organized by frequency, with collocation tables.',
          links: [{ text: 'Access List', to: '/vocabulary-exercises/awl' }],
        },
        {
          title: 'False Friends by L1',
          image: '/Images/speaking.webp',
          desc: 'Quick-reference tables for Spanish, Arabic, Chinese, Russian, and Japanese speakers.',
          links: [{ text: 'View Tables', to: '/resources/false-friends' }],
        },
      ],
    },
    {
      level: 'pron',
      label: 'Pronunciation Practice',
      tagline: 'Model with confidence',
      cards: [
        {
          title: 'Word Stress Workout',
          image: '/Images/quiz1.webp',
          desc: '50 high-frequency academic words with stress-marking audio and self-check quiz.',
          links: [{ text: 'Practice Now', to: '/speaking-exercises/word-stress' }],
        },
        {
          title: 'Intonation Patterns for Teachers',
          image: '/Images/flashcards1.webp',
          desc: 'Rising/falling for questions, lists, corrections, and encouraging feedback.',
          links: [{ text: 'Watch Video', to: '/videos/teacher-intonation' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Brush Up Your English: Grammar, Vocabulary & Pronunciation for ESL Teachers"
      seoDescription="Targeted self-study guide for English teachers. Master the grammar you explain daily, expand your classroom metalanguage, and sharpen pronunciation with free practice modules."
    />
  );
};

export default BrushUpEnglish;